import { useState, useEffect, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { VerticalScrollableContentWithArrows } from "@/components/ui/vertical-scrollable-content-with-arrows";
import { StepProps } from "@/lib/builder/models/builder.model";
import { ItineraryTotals } from "./models/itinerary.model";
import { ItineraryBuilder } from "./models/itinerary-builder.model";
import { Calendar, MapPin, Plane, Car, Utensils, Ticket, StickyNote, Home, CarTaxiFront, FerrisWheel, CupSoda, MicVocal, FileText, Bed } from "lucide-react";
import { ItineraryActivity } from "./models/activity.model";
import { useAuth } from "@/hooks/useAuth";

const CheckoutStep = ({
    data,
    onDataChange,
    builder,
    setCanProceed,
}: StepProps) => {
    const { userRole } = useAuth();

    // Percentuali di default
    const DEFAULT_P2B_COMMISSION_PERCENTAGE = 20; // Markup Place2Be sul commissionabile
    const DEFAULT_TD_FEE_PERCENTAGE = 70; // Split Travel Designer sul netto

    // Totali calcolati usando le percentuali salvate nel DB
    const [totals, setTotals] = useState<ItineraryTotals>({
        commisionable_total: 0,
        not_commisionable_total: 0,
        p2b_commission_percentage: DEFAULT_P2B_COMMISSION_PERCENTAGE,
        td_fee_percentage: DEFAULT_TD_FEE_PERCENTAGE,
        custom_fee: 0,
        p2b_markup_gross: 0,
        vat_amount: 0,
        p2b_markup_net: 0,
        td_fee_amount: 0,
        p2b_fee_amount: 0,
        total: 0,
    });

    // Inizializza builder se non passato
    const itineraryBuilder = useMemo(() => {
        if (builder) return builder;

        if (data?.start_date && data?.end_date && data?.itinerary_days) {
            const newBuilder = new ItineraryBuilder();
            newBuilder.loadFromArray(data.itinerary_days, data.start_date, data.end_date);
            return newBuilder;
        }
        return null;
    }, [builder, data]);

    useEffect(() => {
        // extractTotals usa le percentuali salvate e ricalcola gli importi
        setTotals(data.extractTotals());
    }, [data]);

    // Default refundable to first option if not set
    useEffect(() => {
        if (data.refundable == null) {
            onDataChange({ refundable: REFUNDABLE_OPTIONS[0].value });
        }
        setCanProceed(true);
    }, []);

    const REFUNDABLE_OPTIONS: { value: 'full' | 'partial' | 'non_refundable'; label: string; description: string }[] = [
        {
            value: 'non_refundable',
            label: 'Non refundable',
            description: 'No refunds will be issued for cancellations, changes, or no-shows.',
        },
        {
            value: 'partial',
            label: 'Partially refundable',
            description: 'A cancellation fee may apply, and only part of the total amount may be refunded depending on when you cancel.',
        },
        {
            value: 'full',
            label: 'Refundable',
            description: 'This package is refundable according to the cancellation terms shown at booking.',
        },
    ];

    // Helper per verificare se una percentuale è stata modificata dal default
    const isPercentageModified = (savedPercentage: number, defaultPercentage: number): boolean => {
        return Math.abs(savedPercentage - defaultPercentage) > 0.01; // Tolleranza per arrotondamenti
    };

    // Format price with 2 decimal places
    const formatPrice = (value: number | null | undefined): string => {
        if (value == null) return '-';
        return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    // Helper function to safely access cost property
    const getActivityCost = (activity: ItineraryActivity): number => {
        return (activity as { cost?: number }).cost || 0;
    };

    // Helper function to check if activity should be charged
    const isChargeable = (activity: ItineraryActivity): boolean => {
        return activity.include_price && getActivityCost(activity) > 0;
    };

    // Helper function to check if activity should be shown in checkout
    const shouldShowInCheckout = (activity: ItineraryActivity): boolean => {
        // Escludi attività OUT (check-out)
        if (activity.type === 'accommodation' && (activity as any).inOrOutActivity === 'out') {
            return false;
        }
        return getActivityCost(activity) > 0;
    };

    // Format date for display
    const formatDate = (dateStr: string): string => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
        });
    };

    // Get activity icon and color based on type
    const getActivityIcon = (activity: ItineraryActivity) => {
        const type = activity.type;

        switch (type) {
            case 'accommodation':
                return { icon: Bed, color: 'text-gray-900' };
            case 'flight':
                return { icon: Plane, color: 'text-gray-900' };
            case 'transportation':
                return { icon: CarTaxiFront, color: 'text-gray-900' };
            case 'activity':
                return { icon: FerrisWheel, color: 'text-gray-900' };
            case 'food-drinks':
                return { icon: CupSoda, color: 'text-gray-900' };
            case 'events':
                return { icon: MicVocal, color: 'text-gray-900' };
            case 'notes':
                return { icon: FileText, color: 'text-gray-900' };
            default:
                return { icon: MapPin, color: 'text-gray-900' };
        }
    };

    return (
        <div className="w-full h-full mx-auto p-5 flex items-start justify-center">
            <div className="flex flex-col sm:flex-row gap-6 w-full md:w-full lg:w-[90%] xl:w-[90%] h-full sm:items-start">
                {/* Left Card - Activities Breakdown */}
                <Card className="flex flex-col w-full sm:w-[40%] md:w-[50%] lg:w-[60%] h-full max-h-full overflow-hidden relative">
                    {/* <CardHeader className="flex-shrink-0">
                        <CardTitle className="text-lg font-semibold text-black">Activities Breakdown</CardTitle>
                    </CardHeader> */}

                    <CardContent className="flex-1 min-h-0 p-0">
                        <VerticalScrollableContentWithArrows
                            viewportClassName="p-4"
                        >
                        <div className="space-y-2 pr-2">
                            {itineraryBuilder?.getAllDays().map((day) => {
                                const dayActivities = day.activities.filter(a => shouldShowInCheckout(a));

                                if (dayActivities.length === 0) return null;

                                return (
                                    <div key={day.date} className="space-y-2">
                                        {/* Day Header */}
                                        <div className="flex items-center gap-2 pb-2 border-b border-gray-200">
                                            <Calendar className="w-4 h-4 text-gray-500" />
                                            <span className="text-sm font-semibold text-gray-900">
                                                {formatDate(day.date)}
                                            </span>
                                        </div>

                                        {/* Activities for this day */}
                                        <div className="space-y-2 pl-6">
                                            {dayActivities.map((activity) => {
                                                const chargeable = isChargeable(activity);
                                                const { icon: Icon, color } = getActivityIcon(activity);

                                                return (
                                                    <div
                                                        key={activity.id}
                                                        className={`flex items-start justify-between ${!chargeable ? 'opacity-40' : ''}`}
                                                    >
                                                        <div className="flex items-start gap-2 flex-1">
                                                            <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${chargeable ? color : 'text-gray-400'}`} />
                                                            <div className="flex-1">
                                                                <span className={`text-sm ${chargeable ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                                                                    {activity.title}
                                                                </span>
                                                                {!chargeable && (
                                                                    <span className="ml-2 text-xs text-gray-400 italic">
                                                                        (not charged)
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <span className={`text-sm ${chargeable ? 'text-gray-900 font-semibold' : 'text-gray-400'} ml-4 flex-shrink-0`}>
                                                            €{formatPrice(getActivityCost(activity))}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        </VerticalScrollableContentWithArrows>
                    </CardContent>
                </Card>

                {/* Right column - Revenue Breakdown + Refundable Policy */}
                <VerticalScrollableContentWithArrows
                    className="flex flex-col gap-6 w-full sm:w-[60%] md:w-[50%] lg:w-[40%] h-full overflow-y-auto min-h-0"
                    viewportClassName="flex flex-col gap-6"
                >

                {/* Revenue Breakdown Card */}
                <Card className="flex flex-col w-full h-auto max-h-full">
                    {/* <CardHeader className="flex-shrink-0">
                        <CardTitle className="text-lg font-semibold text-black">Revenue Breakdown</CardTitle>
                    </CardHeader> */}

                    <CardContent className="p-6 flex flex-col flex-shrink-0">
                        <div className="space-y-3">
                            {/* Commissionable Total */}
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-black">Commissionable total</span>
                                    <span className="text-sm font-medium text-gray-900">
                                        €{formatPrice(totals?.commisionable_total)}
                                    </span>
                                </div>
                            </div>

                            {/* Separator */}
                            <div className="border-t border-gray-300"></div>

                            {/* Markup (Gross) */}
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center justify-between">
                                    <span className="text-sm font-medium text-black">Markup Place2Be</span>
                                    <span className="text-sm font-medium text-gray-900">
                                        €{formatPrice(totals?.p2b_markup_gross)}
                                    </span>
                                </div>
                                <div className="text-xs text-gray-500">
                                    (<span className="font-semibold">
                                        {totals?.p2b_commission_percentage != null ? totals.p2b_commission_percentage : DEFAULT_P2B_COMMISSION_PERCENTAGE}%
                                    </span> of Commissionable total)
                                </div>
                            </div>

                            {/* VAT and Net (if admin or travel designer) */}
                            {(userRole !== 'traveler') && (
                                <>
                                    <div className="flex flex-col gap-1 pl-4 border-l-2 border-gray-200">
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs font-medium text-gray-600">Markup Net (IVA deducted)</span>
                                            <span className="text-xs font-medium text-gray-700">
                                                €{formatPrice(totals?.p2b_markup_net)}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-500">VAT (22%)</span>
                                            <span className="text-xs text-gray-600">
                                                €{formatPrice(totals?.vat_amount)}
                                            </span>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Your Earnings / Designer Earnings */}
                            {userRole !== 'traveler' && (
                                <div className="flex flex-col gap-1">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-black">
                                            Travel Advisor fee
                                        </span>
                                        <span className="text-sm font-medium text-gray-900">
                                            €{formatPrice(totals?.td_fee_amount)}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        ({isPercentageModified(totals.td_fee_percentage, DEFAULT_TD_FEE_PERCENTAGE) && (
                                            <span className="line-through mr-1">{DEFAULT_TD_FEE_PERCENTAGE}%</span>
                                        )}
                                        <span className={isPercentageModified(totals.td_fee_percentage, DEFAULT_TD_FEE_PERCENTAGE) ? 'font-semibold' : ''}>
                                            {totals?.td_fee_percentage ?? DEFAULT_TD_FEE_PERCENTAGE}%
                                        </span> of Net markup)
                                    </div>

                                </div>
                            )}

                            {/* Place2Be Earnings (admin and travel designer) */}
                            {userRole !== 'traveler' && (
                                <div className="flex flex-col gap-1">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-black">Place2Be fee</span>
                                        <span className="text-sm font-medium text-gray-900">
                                            €{formatPrice(totals?.p2b_fee_amount)}
                                        </span>
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        ({100 - (totals?.td_fee_percentage ?? DEFAULT_TD_FEE_PERCENTAGE)}% of Net markup)
                                    </div>
                                </div>
                            )}

                            {/* Custom Fee (if set) */}
                            {totals?.custom_fee > 0 && (
                                <>
                                    <div className="border-t border-gray-300"></div>
                                    <div className="flex flex-col gap-1">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-black">Custom Fee</span>
                                            <span className="text-sm font-medium text-gray-900">
                                                €{formatPrice(totals?.custom_fee)}
                                            </span>
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            (Additional fee set by admin)
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Total Section - Bottom */}
                        <div className="space-y-3 pt-6">
                            {/* Separator */}
                            <div className="border-t-2 border-gray-400"></div>

                            {/* Total (with fee) */}
                            <div className="flex items-center justify-between">
                                <span className="text-lg font-semibold text-black">Total</span>
                                <span className="text-lg font-semibold text-gray-900">
                                    €{formatPrice(totals?.total)}
                                </span>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Refundable Policy Card */}
                <Card className="flex flex-col w-full h-auto border-0 shadow-none p-0">
                    <CardContent className="p-0">
                        <RadioGroup
                            value={data.refundable ?? REFUNDABLE_OPTIONS[0].value}
                            onValueChange={(value) =>
                                onDataChange({ refundable: value as 'full' | 'partial' | 'non_refundable' })
                            }
                            className="gap-0"
                        >
                            {REFUNDABLE_OPTIONS.map((option, index) => (
                                <label
                                    key={option.value}
                                    className={`flex items-start gap-3 p-3 cursor-pointer transition-colors border-x border-gray-200
                                        ${index === 0 ? 'rounded-t-lg border-t' : ''}
                                        ${index === REFUNDABLE_OPTIONS.length - 1 ? 'rounded-b-lg border-b' : ''}
                                        border-b border-gray-200
                                        ${data.refundable === option.value ? 'bg-gray-50' : 'hover:bg-gray-50'}
                                    `}
                                >
                                    <RadioGroupItem value={option.value} id={option.value} className="mt-0.5 flex-shrink-0" />
                                    <div className="flex flex-col gap-0.5">
                                        <Label htmlFor={option.value} className="text-sm font-medium text-black cursor-pointer">
                                            {option.label}
                                        </Label>
                                        <span className="text-xs text-gray-500 leading-snug">
                                            {option.description}
                                        </span>
                                    </div>
                                </label>
                            ))}
                        </RadioGroup>
                    </CardContent>
                </Card>

                </VerticalScrollableContentWithArrows>{/* end right column */}
            </div>
        </div>
    );
};

export default CheckoutStep;
